import { useCallback } from 'react';
import { getSectionId, SectionId } from '../utils/get-section-id';
import type { ScrollableSectionProps } from '../types/scrollable-section';

/**
 * Hook for components that render scrollable sections with viewport-prefixed IDs.
 * 
 * Provides a bound helper function for generating section IDs consistently
 * across all scrollable section components.
 * 
 * @param viewport - The viewport identifier ('desktop' or 'mobile')
 * @returns Object with getSectionId helper bound to the viewport
 * 
 * @example
 * function MyScrollableSection({ viewport }: ScrollableSectionProps) {
 *   const { getSectionId } = useScrollableSection(viewport);
 *   
 *   return <section id={getSectionId('overview')}>...</section>;
 *   // → <section id="desktop-overview"> or <section id="mobile-overview">
 * }
 */
export function useScrollableSection(viewport: ScrollableSectionProps['viewport']) {
  /**
   * Generate viewport-prefixed section ID.
   * Bound to the component's viewport for convenience.
   */
  const getSectionIdBound = useCallback(
    (baseId: SectionId) => getSectionId(baseId, viewport),
    [viewport]
  );

  return {
    getSectionId: getSectionIdBound,
  };
}
